import type { FC } from "react";

import { useUsageStats } from "../../hooks/useUsageStats";
import { DataPanel } from "../common/DataPanel";
import { DataState } from "../common/DataState";
import { UsageBreakdownCharts } from "./UsageBreakdownCharts";
import { UsageContextEfficiencyPanel } from "./UsageContextEfficiencyPanel";
import { UsageEntriesTable } from "./UsageEntriesTable";
import { UsageFiltersPanel } from "./UsageFiltersPanel";
import { UsageHeroMetrics } from "./UsageHeroMetrics";
import { UsageSecondaryMetrics } from "./UsageSecondaryMetrics";
import { UsageTimeseriesChart } from "./UsageTimeseriesChart";
import { UsageTimeseriesStackedChart } from "./UsageTimeseriesStackedChart";
import { UsageTokenCompositionChart } from "./UsageTokenCompositionChart";

export const UsageView: FC = () => {
  const usage = useUsageStats({
    bucketUnit: "day",
    debounceMs: 250,
    includeBreakdowns: true,
    includeTimeseries: true,
    initialPageSize: 50,
  });

  return (
    <div className="space-y-4">
      {/* Header with collapsible filters */}
      <DataPanel
        title="Usage & Cost"
        subtitle="Filter and explore token spend across providers, models, and stages."
        icon={
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .672-3 1.5S10.343 11 12 11s3 .672 3 1.5S13.657 14 12 14m0-6V7m0 7v1m-6 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        }
        iconColor="emerald"
        className="hover:shadow-none"
      >
        <UsageFiltersPanel
          filters={usage.filters}
          isLoading={usage.isLoading}
          onSetFilters={usage.setFilters}
          onSetDateRange={usage.setDateRange}
          onReset={usage.reset}
          onRefresh={usage.refresh}
        />
      </DataPanel>

      <DataState
        loading={usage.isLoading && !usage.summary}
        error={usage.error?.message ?? null}
        empty={
          !usage.isLoading &&
          Boolean(usage.summary) &&
          usage.totalRows === 0 &&
          (usage.summary?.contextEfficiency.readCount ?? 0) === 0
        }
        emptyTitle="No usage data"
        emptyDescription="No usage records match the current filters."
        className="space-y-4"
      >
        {/* Hero Metrics - Most prominent */}
        <UsageHeroMetrics totals={usage.totals} />

        <UsageContextEfficiencyPanel
          summary={usage.summary?.contextEfficiency ?? EMPTY_CONTEXT_EFFICIENCY}
        />

        {/* Timeseries Chart - Full width */}
        <UsageTimeseriesChart buckets={usage.summary?.timeseries?.buckets} />

        {/* Token type breakdown over time */}
        <UsageTimeseriesStackedChart
          buckets={usage.summary?.timeseries?.buckets}
        />

        {/* Secondary Metrics - Detailed breakdown */}
        <UsageSecondaryMetrics totals={usage.totals} />

        {/* Token composition per model */}
        <UsageTokenCompositionChart
          breakdowns={usage.summary?.breakdowns ?? null}
        />

        {/* Breakdown Charts with Donut visualizations */}
        <UsageBreakdownCharts breakdowns={usage.summary?.breakdowns ?? null} />

        {/* Entries Table - At bottom */}
        <UsageEntriesTable
          entries={usage.entriesRows}
          total={usage.totalRows}
          page={usage.page}
          pageSize={usage.pageSize}
          isLoading={usage.isLoading}
          errorMessage={usage.error?.message ?? null}
          onPageChange={usage.setPage}
        />
      </DataState>
    </div>
  );
};

const EMPTY_CONTEXT_EFFICIENCY = {
  avoidedPromptTokens: 0,
  cacheHitCount: 0,
  coveredMatchCount: 0,
  exactMatchCount: 0,
  pricedHitCount: 0,
  readCount: 0,
  returnedTokens: 0,
  savedTokens: 0,
};

export default UsageView;
